<script>
export default {
  props: {
    search: {
      type: String,
      default: ''
    },
    tags: {
      type: Array,
      default: null
    },
    activeTagId: {
      type: [String, Number],
      default: null
    },
    showSearch: {
      type: Boolean,
      default: true
    },
    // Optional quota envelope from the list endpoint: { resource, limit, current, remaining }.
    // When passed, renders a counter chip on the right side of the toolbar.
    quota: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      showTags: false,
      searchValue: this.search
    }
  },
  watch: {
    search(val) {
      this.searchValue = val;
    },
    searchValue(val) {
      this.$emit('update:search', val);
    }
  },
  computed: {
    hasTags() {
      return this.tags && this.tags.length > 0;
    },
    quotaPercentage() {
      if (!this.quota || !this.quota.limit) return 0;
      return Math.min(100, Math.round((this.quota.current / this.quota.limit) * 100));
    },
    // 'normal' | 'warning' | 'danger' — warning fires at 80% used.
    quotaState() {
      if (!this.quota) return 'normal';
      if (this.quota.remaining === 0) return 'danger';
      if (this.quotaPercentage >= 80) return 'warning';
      return 'normal';
    },
    // Tailwind colour for the progress-bar fill, keyed off state.
    quotaBarFillClass() {
      if (this.quotaState === 'danger') return 'tw-bg-red-500';
      if (this.quotaState === 'warning') return 'tw-bg-amber-500';
      return 'tw-bg-emerald-500';
    },
    // Count text colour — mirrors the bar so the number reads as a status value.
    quotaCountClass() {
      if (this.quotaState === 'danger') return 'tw-text-red-600';
      if (this.quotaState === 'warning') return 'tw-text-amber-600';
      return 'tw-text-emerald-600';
    },
    // Unique id so multiple <searchBar> instances on the same page don't share popover targets.
    quotaToggleId() {
      return `quota-toggle-${this._uid}`;
    }
  },
  methods: {
    selectTag(tagId) {
      this.$emit('tag-selected', tagId);
    }
  }
}
</script>

<template>
  <div class="toolbar-wrapper">
    <div class="card toolbar-card">
      <div class="toolbar-row">
        <div class="toolbar-filters">
          <div v-if="$scopedSlots.pre || showSearch" class="toolbar-main">
            <slot name="pre"></slot>
            <b-form-input v-if="showSearch" v-model="searchValue" :placeholder="$t('searchPlaceholder')" class="form-control toolbar-search"
                          debounce="400" type="search"
            />
          </div>
          <div class="toolbar-forms">
            <slot name="forms"></slot>
            <b-button v-if="hasTags" v-b-tooltip.hover
                      :title="$t('global.filterByTag.title')"
                      :class="['btn-icon', 'toolbar-tag-toggle', { active: showTags || activeTagId !== null }]"
                      data-testid="search-tags-toggle"
                      @click="showTags = !showTags"
            >
              <!-- Lucide "tags" -->
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"
              >
                <path d="m15 5 6.3 6.3a2.4 2.4 0 0 1 0 3.4L17 19"/>
                <path d="M9.586 5.586A2 2 0 0 0 8.172 5H3a1 1 0 0 0-1 1v5.172a2 2 0 0 0 .586 1.414L8.29 18.29a2.426 2.426 0 0 0 3.42 0l3.58-3.58a2.426 2.426 0 0 0 0-3.42z"/>
                <circle cx="6.5" cy="9.5" r=".5" fill="currentColor"/>
              </svg>
            </b-button>
          </div>
        </div>
        <div v-if="$scopedSlots.buttons || quota" class="toolbar-actions">
          <slot name="buttons"></slot>
          <template v-if="quota">
            <button :id="quotaToggleId"
                    type="button"
                    class="toolbar-quota-icon"
                    :class="'toolbar-quota-icon--' + quotaState"
                    :aria-label="$t('quota.chip.title')"
            >
              <i class="fe-bar-chart-2"></i>
            </button>
            <b-popover :target="quotaToggleId" triggers="hover focus" placement="bottom" custom-class="quota-popover">
              <div class="tw-flex tw-flex-col tw-gap-2 tw-min-w-[200px]">
                <div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
                  <span class="tw-text-sm tw-font-semibold tw-text-gray-900">{{ $t('quota.chip.title') }}</span>
                  <span class="tw-text-sm tw-font-semibold" :class="quotaCountClass">{{ quota.current }} / {{ quota.limit }}</span>
                </div>
                <div class="tw-h-2 tw-bg-gray-100 tw-rounded-full tw-overflow-hidden">
                  <div class="tw-h-full tw-rounded-full tw-transition-all"
                       :class="quotaBarFillClass"
                       :style="{ width: quotaPercentage + '%' }"></div>
                </div>
                <p class="tw-text-xs tw-text-gray-400 tw-leading-snug tw-m-0">{{ $t('quota.chip.note') }}</p>
              </div>
            </b-popover>
          </template>
        </div>
      </div>
      <transition name="tags-slide">
      <div v-if="showTags && hasTags" class="toolbar-tags">
        <button :class="{ active: activeTagId === null }" class="tag-chip"
                @click="selectTag(null)"
        >
          {{ $t('global.viewAll') }}
        </button>
        <button v-for="tag in tags" :key="tag.id"
                :class="{ active: tag.id === activeTagId }" class="tag-chip" data-testid="search-tag"
                @click="selectTag(tag.id)"
        >
          <i class="fe-tag"></i> {{ tag.text }}
        </button>
      </div>
      </transition>
    </div>
  </div>
</template>

<style lang="scss">
.toolbar-card {
  padding: 1.4rem 1.75rem !important;
  margin-bottom: 1.25rem;

  // Lines the controls up with the page title and the list cards' edges.
  @media (max-width: 767px) {
    padding: 12px !important;
    margin-bottom: 12px !important;
  }
}

.toolbar-row {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;

  // Phones flow every control in one wrapping list, so the tag toggle can lead the actions line.
  @media (max-width: 767px) {
    flex-wrap: wrap;
    align-items: center;
    gap: 1.15rem 0.75rem;
  }
}

// The search line sits beside the actions; the selects join it when they fit and
// move to the next line together when they don't. The taller row gap leaves room
// for their floating labels.
.toolbar-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.15rem 0.75rem;
  flex: 1 1 0;
  min-width: 0;

  .form-group {
    margin-bottom: 0;
    margin-right: 0 !important;
    margin-left: 0 !important;
  }

  .form-control, .custom-select, .btn {
    height: 38px;
    border-radius: 6px;
    border-color: $color_border;
  }

  // Selects take their longest option's width, so the picked value is never cut.
  select {
    min-width: 80px;
    max-width: 320px;
  }

  @media (max-width: 767px) {
    display: contents;

    .custom-select {
      width: 100%;
      max-width: none;
    }
  }
}

.toolbar-main {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1 1 auto;
  min-width: 0;
}

// Grows far faster than the search line, so the search only widens on a line of its own.
// The tag toggle sits at its end, so it wraps together with the selects.
.toolbar-forms {
  display: flex;
  align-items: center;
  gap: 1.15rem 0.75rem;
  flex: 1000 0 auto;

  @media (max-width: 767px) {
    display: contents;

    > * {
      flex: 1 1 auto;
    }
  }
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;

  .btn {
    height: 38px;
    border-radius: 6px;
  }

  // The primary action (bare or inside its tooltip span) fills the line.
  @media (max-width: 767px) {
    flex: 1 1 auto;

    > .btn-primary, > span {
      flex: 1 1 auto;
    }

    > span > .btn {
      width: 100%;
    }
  }
}

.tags-slide-enter-active {
  animation: tagsIn 0.2s ease-out;
}

.tags-slide-leave-active {
  animation: tagsIn 0.15s ease-in reverse;
}

@keyframes tagsIn {
  from {
    opacity: 0;
    margin-top: 0;
    padding-top: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    max-height: 200px;
  }
}

.toolbar-tags {
  padding-top: 0.75rem;
  margin-top: 0.75rem;
  padding-bottom: 0.25rem;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.toolbar-search.form-control {
  width: 220px !important;
  flex: 1 1 auto !important;
  min-width: 0;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:not(:placeholder-shown) {
    border-color: $color_primary;
    background-color: rgba($color_primary, 0.04);
  }
}

.toolbar-quota-icon {
  width: 38px;
  height: 38px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid #e5e7eb; // gray-200
  color: #6b7280; // gray-500
  font-size: 1.05rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover, &:focus {
    background: #f9fafb; // gray-50
    border-color: #d1d5db; // gray-300
    color: $color_text;
    outline: none;
  }

  &--warning {
    background: #fffbeb; // amber-50
    border-color: #fde68a; // amber-200
    color: #b45309; // amber-700

    &:hover, &:focus {
      background: #fef3c7; // amber-100
      border-color: #fcd34d; // amber-300
      color: #92400e; // amber-800
    }
  }

  &--danger {
    background: #fef2f2; // red-50
    border-color: #fecaca; // red-200
    color: #b91c1c; // red-700

    &:hover, &:focus {
      background: #fee2e2; // red-100
      border-color: #fca5a5; // red-300
      color: #991b1b; // red-800
    }
  }
}

.quota-popover {
  .popover-body {
    padding: 0.9rem 1.05rem;
  }
}

// Same outlined icon button as the actions; primary while the strip is open or a tag filters the list.
.toolbar-tag-toggle.btn {
  width: 38px;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  flex: none;
  box-shadow: none !important;
}

.toolbar-toggle-group {
  display: inline-flex;
  border: 1px solid $color_border;
  border-radius: 8px;
  overflow: hidden;
  height: 38px;
  flex-shrink: 0;

  .toolbar-toggle-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 100%;
    background: #fff;
    border: none;
    color: #6b7280;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.15s ease;
    padding: 0;
    outline: none;

    &:not(:last-child) {
      border-right: 1px solid $color_border;
    }

    &:hover {
      color: $color_text;
      background: #f9fafb;
    }

    &.active {
      background: $color_primary;
      color: #fff;

      &:hover {
        background: darken($color_primary, 5%);
      }
    }
  }
}

.floating-label-group {
  position: relative;

  label {
    position: absolute;
    top: -7px;
    left: 10px;
    font-size: 0.62rem;
    font-weight: 500;
    color: #adb5bd;
    background: #fff;
    padding: 0 4px;
    z-index: 1;
    line-height: 1;
    white-space: nowrap;
  }
}

.tag-chip {
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid $color_border;
  background: #fff;
  color: $color_text;
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  line-height: 1.4;

  &:hover {
    border-color: $color_primary;
    color: $color_primary;
    background: rgba($color_primary, 0.04);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba($color_primary, 0.15);
  }

  &.active {
    background: $color_primary;
    color: #fff;
    border-color: $color_primary;
    box-shadow: 0 1px 3px rgba($color_primary, 0.25);
  }

  i {
    font-size: 0.75rem;
  }
}
</style>
